/**
 * "Alcance a clientes" (client outreach) — shared contracts for the suggested /
 * scheduled / sent messages that follow up with a client about their documents
 * (solicitados, por vencer, vencidos). Reuses the notification channel already
 * defined for on-demand document-request notifications (email/whatsapp) so the
 * two features share one source of truth for "how we contact a client".
 *
 * `composeOutreachMessage` is the PURE, regulator-reproducible core: no I/O, no
 * dates from the wall clock — deterministic for a given input, mirroring
 * `composeDocumentRequestMessage` (§7). Every user-facing string is es-PA
 * (Ley 23 de Panamá).
 */
import { z } from 'zod';
import { notificationChannelSchema } from './schemas.js';
/** Lifecycle of an outreach message, from suggestion to terminal state. */
export const OUTREACH_STATUSES = [
    'draft',
    'scheduled',
    'sending',
    'sent',
    'failed',
    'canceled',
];
/** Why the outreach message is being suggested/sent. */
export const OUTREACH_REASONS = [
    'documento_solicitado',
    'documento_por_vencer',
    'documento_vencido',
];
// ---------------------------------------------------------------------------
// Zod input schemas.
// ---------------------------------------------------------------------------
/** Filters for suggesting outreach messages across the tenant's expedientes. */
export const generateOutreachSchema = z.object({});
const updateOutreachFields = z.object({
    subject: z.string().trim().min(1).max(200).optional(),
    body: z.string().trim().min(1).max(4000).optional(),
    channels: z.array(notificationChannelSchema).nonempty().optional(),
    // ISO datetime to schedule the send, or null to unschedule (back to draft).
    scheduledAt: z.string().datetime().nullable().optional(),
});
export const updateOutreachSchema = updateOutreachFields.refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Al menos un campo es requerido' });
const REASON_SUBJECT = {
    documento_solicitado: 'Solicitud de documentos',
    documento_por_vencer: 'Documentos próximos a vencer',
    documento_vencido: 'Documentos vencidos — acción requerida',
};
/**
 * Compose the plain-text es-PA subject + body for a client-outreach message.
 * Deterministic for a given input — no I/O. Plain text is the audit-of-record
 * `body`; the email/WhatsApp adapters render it as-is or as HTML.
 */
export function composeOutreachMessage(input) {
    const { reason, clientName, tenantName, expedienteRef, documentNames } = input;
    const subject = `${REASON_SUBJECT[reason]} — expediente ${expedienteRef}`;
    const list = documentNames.length > 0
        ? documentNames.map((name) => `- ${name}`).join('\n')
        : '- Documentos de debida diligencia requeridos por la regulación.';
    const lines = [];
    lines.push(`Estimado/a ${clientName}:`);
    lines.push('');
    if (reason === 'documento_solicitado') {
        lines.push(`Le escribimos respecto a su expediente ${expedienteRef}. Para mantener su información ` +
            'al día conforme a la Ley 23 de Panamá (prevención de blanqueo de capitales), ' +
            'necesitamos que nos haga llegar los siguientes documentos:');
        lines.push('');
        lines.push(list);
        lines.push('');
        lines.push('Por favor envíenos estos documentos a la brevedad posible. Si ya los envió o tiene ' +
            'alguna duda, responda a este mensaje y con gusto le ayudamos.');
    }
    else if (reason === 'documento_por_vencer') {
        lines.push(`Le informamos que, respecto a su expediente ${expedienteRef}, los siguientes ` +
            'documentos están próximos a vencer:');
        lines.push('');
        lines.push(list);
        lines.push('');
        lines.push('Le agradecemos hacernos llegar la versión vigente antes de la fecha de vencimiento, ' +
            'para mantener su expediente al día conforme a la Ley 23 de Panamá.');
    }
    else {
        lines.push(`Le informamos que, respecto a su expediente ${expedienteRef}, los siguientes ` +
            'documentos ya están vencidos:');
        lines.push('');
        lines.push(list);
        lines.push('');
        lines.push('Le solicitamos hacernos llegar la versión vigente lo antes posible, ya que la ' +
            'regulación (Ley 23 de Panamá) exige mantener esta información actualizada.');
    }
    lines.push('');
    lines.push('Quedamos atentos a sus comentarios.');
    lines.push('');
    lines.push('Atentamente,');
    lines.push(tenantName);
    return { subject, body: lines.join('\n') };
}
//# sourceMappingURL=outreach.js.map